import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, RotateCcw,
  ChevronRight, ChevronLeft, Trophy, Lock, Loader2, Eye
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────
   Formats remaining cooldown time as MM:SS or HH:MM:SS
───────────────────────────────────────────────────────── */
function formatCountdown(ms) {
  if (ms <= 0) return '00:00';
  const totalSec = Math.floor(ms / 1000);
  const hrs = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hrs > 0) return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/* ─────────────────────────────────────────────────────────
   Main QuizEngine Component
   Props:
   - quizData: { questions, isMandatory, allowRetakes, cooldownHours, passingScore }
   - lessonId: string (unique lesson ID – used as Firestore doc key)
   - courseId: string
   - currentUser: Firebase Auth user object
   - onPassStatusChange: (passed: boolean) => void  → emitted to parent to lock/unlock Next button
───────────────────────────────────────────────────────── */
export default function QuizEngine({ quizData, lessonId, courseId, currentUser, onPassStatusChange }) {
  const { questions = [], isMandatory = false, allowRetakes = true, cooldownHours = 0, passingScore = 60 } = quizData || {};

  // ── State
  const [phase, setPhase] = useState('loading'); // loading | cooldown | taking | result
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});    // { [qIdx]: optionIdx }
  const [result, setResult] = useState(null);    // { score, passed, submittedAt }
  const [prevAttempt, setPrevAttempt] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const resultDocId = `${courseId}_${lessonId}`;

  // ── Load previous attempt from Firestore
  useEffect(() => {
    if (!currentUser) return;
    const load = async () => {
      try {
        const ref = doc(db, 'users', currentUser.uid, 'quizResults', resultDocId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setPrevAttempt(data);
          if (data.answers) {
            setAnswers(data.answers);
          }
          if (data.passed) {
            setResult(data);
            setPhase('result');
            onPassStatusChange?.(true, data.score);
            return;
          }
          // Check cooldown
          if (cooldownHours > 0 && data.submittedAt) {
            const elapsed = Date.now() - data.submittedAt.toMillis?.() || 0;
            const cooldownMs = cooldownHours * 3600 * 1000;
            if (elapsed < cooldownMs) {
              setCountdown(cooldownMs - elapsed);
              setPhase('cooldown');
              return;
            }
          }
        }
        setPhase('taking');
      } catch (e) {
        console.error(e);
        setPhase('taking');
      }
    };
    load();
  }, [currentUser, resultDocId, cooldownHours]);

  // ── Countdown timer
  useEffect(() => {
    if (phase !== 'cooldown') return;
    const interval = setInterval(() => {
      setCountdown(prev => {
        const next = prev - 1000;
        if (next <= 0) { clearInterval(interval); setPhase('taking'); return 0; }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // ── Submit Quiz
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correctIndex) correct++;
    });
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= passingScore;
    const payload = { score, passed, correct, total: questions.length, submittedAt: serverTimestamp(), answers };

    try {
      if (currentUser) {
        const ref = doc(db, 'users', currentUser.uid, 'quizResults', resultDocId);
        await setDoc(ref, payload, { merge: true });
      }
    } catch (e) {
      console.error('Failed to save quiz result:', e);
    }

    setResult({ score, passed, correct, total: questions.length });
    setPhase('result');
    onPassStatusChange?.(passed, score);
    setIsSubmitting(false);
  }, [answers, questions, passingScore, currentUser, resultDocId, isSubmitting, onPassStatusChange]);

  // ── Retake
  const handleRetake = () => {
    setAnswers({});
    setCurrentQ(0);
    setResult(null);
    setPhase('taking');
    onPassStatusChange?.(false);
  };

  if (!questions.length) {
    return (
      <div className="rounded-2xl bg-slate-50 dark:bg-zinc-900/80 border border-slate-200 dark:border-white/10 p-10 text-center text-slate-500 dark:text-zinc-400">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-primary-500/60" />
        <p className="font-bold">لم يتم إضافة أسئلة للاختبار بعد.</p>
      </div>
    );
  }

  const answered = Object.keys(answers).length;
  const progressPct = Math.round((answered / questions.length) * 100);

  /* ── PHASE: LOADING */
  if (phase === 'loading') return (
    <div className="flex justify-center items-center py-24">
      <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
    </div>
  );

  /* ── PHASE: COOLDOWN */
  if (phase === 'cooldown') return (
    <div className="rounded-3xl bg-white dark:bg-gradient-to-br dark:from-zinc-900 dark:to-black border border-slate-200 dark:border-primary-500/20 p-10 text-center space-y-6 shadow-xl">
      <div className="w-20 h-20 rounded-full bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/30 mx-auto flex items-center justify-center">
        <Clock className="w-10 h-10 text-primary-500 dark:text-primary-400" />
      </div>
      <div>
        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">جرب مرة أخرى لاحقاً</h3>
        <p className="text-slate-500 dark:text-zinc-400 text-md">لم تجتز الاختبار. يمكنك إعادة المحاولة بعد انتهاء وقت الانتظار.</p>
      </div>
      <div className="font-mono text-5xl font-black text-primary-600 dark:text-primary-400 tracking-widest bg-primary-50 dark:bg-primary-500/10 py-4 px-8 rounded-2xl inline-block border border-primary-100 dark:border-primary-500/20 shadow-inner" dir="ltr">
        {formatCountdown(countdown)}
      </div>
      {prevAttempt && (
        <p className="text-md text-slate-400 dark:text-zinc-500">آخر نتيجة: <span className="text-primary-600 dark:text-primary-400 font-bold">{prevAttempt.score}%</span></p>
      )}
    </div>
  );

  /* ── PHASE: RESULT */
  if (phase === 'result' && result) {
    const passed = result.passed;

    if (showDetails) {
      return (
        <div className="rounded-2xl border p-6 text-right bg-white dark:bg-gradient-to-br dark:from-[#0d0d0d] dark:to-zinc-900 border-slate-200 dark:border-white/10 shadow-xl overflow-hidden">
           <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100 dark:border-white/5">
             <button onClick={() => setShowDetails(false)} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-white font-bold transition-all">
               <ChevronRight className="w-5 h-5" /> عودة للنتيجة
             </button>
             <h3 className="text-xl font-black text-slate-900 dark:text-white">تفاصيل الإجابات</h3>
           </div>
           <div className="space-y-8 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
             {questions.map((q, i) => {
               const userAnswer = answers[i];
               return (
                 <div key={i} className="space-y-3">
                   <p className="font-bold text-lg text-slate-800 dark:text-white">
                     <span className="text-primary-600 dark:text-primary-400 font-black ml-2">{i+1}.</span>
                     {q.question}
                   </p>
                   <div className="space-y-2 pr-6">
                     {(q.options || []).map((opt, oi) => {
                       const isSelected = userAnswer === oi;
                       const isOptCorrect = q.correctIndex === oi;
                       
                       let bgClass = "bg-slate-50 border-slate-200 text-slate-600 dark:bg-zinc-900/60 dark:border-zinc-700/80 dark:text-zinc-400";
                       if (isOptCorrect) {
                         bgClass = "bg-emerald-50 border-emerald-500 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/40";
                       } else if (isSelected && !isOptCorrect) {
                         bgClass = "bg-rose-50 border-rose-500 text-rose-800 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/40";
                       }

                       return (
                         <div key={oi} className={`px-4 py-3 rounded-xl border flex items-center justify-between ${bgClass}`}>
                            <span className={isSelected ? "font-bold" : "font-medium"}>{opt}</span>
                            <div className="flex gap-2">
                              {isOptCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                              {isSelected && !isOptCorrect && <XCircle className="w-5 h-5 text-rose-500" />}
                            </div>
                         </div>
                       );
                     })}
                   </div>
                 </div>
               );
             })}
           </div>
        </div>
      );
    }

    return (
      <div className={`rounded-3xl border p-10 text-center space-y-6 shadow-2xl ${passed
        ? 'bg-emerald-50 dark:bg-gradient-to-br dark:from-emerald-950 dark:to-zinc-900 border-emerald-200 dark:border-emerald-500/30'
        : 'bg-rose-50 dark:bg-gradient-to-br dark:from-rose-950 dark:to-zinc-900 border-rose-200 dark:border-rose-500/30'
        }`}>
        <div className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center border-2 ${passed ? 'bg-emerald-100 dark:bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-100 dark:bg-rose-500/10 border-rose-500/40'
          }`}>
          {passed
            ? <Trophy className="w-12 h-12 text-emerald-600 dark:text-emerald-400" />
            : <XCircle className="w-12 h-12 text-rose-600 dark:text-rose-400" />
          }
        </div>
        <div>
          <h3 className={`text-3xl font-black mb-2 ${passed ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
            {passed ? '🎉 أحسنت، اجتزت الاختبار!' : 'لم تجتز الاختبار'}
          </h3>
          <p className="text-lg font-bold  dark:text-zinc-400">
            أجبت على <span className="font-bold text-slate-900 dark:text-white">{result.correct}</span> من <span className="font-bold text-slate-900 dark:text-white">{result.total}</span> سؤال بشكل صحيح
          </p>
        </div>
        {/* Score ring */}
        <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full border-4 text-4xl font-black shadow-inner ${passed ? 'bg-white dark:bg-transparent border-emerald-500 text-emerald-600 dark:text-emerald-300' : 'bg-white dark:bg-transparent border-rose-500 text-rose-600 dark:text-rose-300'
          }`}>
          {result.score}%
        </div>
        <p className="text-lg text-slate-800 dark:text-zinc-500 font-bold">درجة النجاح الموحدة: {passingScore}%</p>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
          <button onClick={() => setShowDetails(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white font-black transition-all shadow-lg">
            <Eye className="w-5 h-5" /> تفاصيل الإجابات
          </button>
          {!passed && allowRetakes && (
            <button onClick={handleRetake}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white font-black transition-all shadow-lg">
              <RotateCcw className="w-5 h-5" /> إعادة الاختبار
            </button>
          )}
        </div>
        {!passed && !allowRetakes && (
          <div className="flex items-center justify-center gap-2 text-md text-slate-500 dark:text-zinc-500 mt-4">
            <Lock className="w-4 h-4" /> إعادة المحاولة غير مسموح بها
          </div>
        )}
        {passed && (
          <p className="text-lg text-emerald-600 dark:text-emerald-500/80 font-black mt-4">✓ عبقري! يمكنك متابعة رحلتك والاتنقال للدرس التالي</p>
        )}
      </div>
    );
  }

  /* ── PHASE: TAKING */
  const q = questions[currentQ];
  const isLast = currentQ === questions.length - 1;
  const allAnswered = answered === questions.length;

  return (
    <div className="rounded-lg bg-white dark:bg-gradient-to-br dark:from-[#0d0d0d] dark:to-zinc-900  dark:border-white/8 overflow-hidden transition-colors">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-2xl font-black text-slate-900 dark:text-white">محطة الاختبار التفاعلي</h3>
          <span className="text-xl font-bold text-slate-400 dark:text-zinc-400"><span className='text-primary-600 dark:text-primary-400'>{currentQ + 1}</span> / {questions.length}</span>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="p-6 min-h-[300px] flex flex-col">
        <p className="text-2xl font-black text-slate-800 dark:text-white mb-8 leading-tight">{q.question}</p>

        <div className="space-y-3 flex-1">
          {(q.options || []).map((option, oi) => {
            const selected = answers[currentQ] === oi;
            return (
              <button
                key={oi}
                onClick={() => setAnswers(prev => ({ ...prev, [currentQ]: oi }))}
                className={`w-full text-right px-5 py-4 rounded-2xl border font-bold transition-all duration-200 flex items-center gap-3 ${selected
                  ? 'bg-primary-50 border-primary-500 text-primary-700 shadow-lg shadow-primary-500/10 dark:bg-primary-500/20 dark:text-primary-200'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 dark:bg-zinc-900/60 dark:border-zinc-700/80 dark:text-zinc-300 dark:hover:bg-white/8 dark:hover:border-white/20'
                  }`}
              >
                <span className={`w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-all ${selected ? 'border-primary-500 bg-primary-500' : 'border-slate-300 dark:border-white/20'
                  }`}>
                  {selected && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
                </span>
                {option}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => setCurrentQ(prev => Math.max(0, prev - 1))}
            disabled={currentQ === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-300 font-black text-md transition-all disabled:opacity-30 shadow-sm"
          >
            <ChevronRight className="w-5 h-5" /> السابق
          </button>

          {isLast ? (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || isSubmitting}
              className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-black text-md transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              {isSubmitting ? 'جاري التصحيح...' : allAnswered ? 'تسليم الإجابات' : `استمر`}
            </button>
          ) : (
            <button
              onClick={() => setCurrentQ(prev => Math.min(questions.length - 1, prev + 1))}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-black text-md transition-all shadow-lg shadow-primary-500/20"
            >
              التالي <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>

        {!allAnswered && isLast && (
          <p className="text-center text-md text-zinc-500 mt-3">
            يجب الإجابة على جميع الأسئلة قبل التسليم
          </p>
        )}
      </div>
    </div>
  );
}
